import { Dispatch, SetStateAction } from "react";

import { DEFAULT_PAGE } from "../../NewSearchPageMVP.constants";
import { AgathaSchemaParamsEnum } from "../../types/IParametersSchema";

const useSearchParamsControls = ({
  searchParams,
  setSearchParams,
}: IUseSearchParamsControls) => {
  // Use getAllSearchParams to get a list of all params
  const getAllSearchParams = () => [...searchParams.entries()];

  // Use deleteSearchParamValues() to remove all search param values for the given param name
  const deleteSearchParamValues = (paramName: string) => {
    if (paramName !== AgathaSchemaParamsEnum.page) {
      searchParams.set(AgathaSchemaParamsEnum.page, DEFAULT_PAGE);
    }
    searchParams.delete(paramName);
    setSearchParams(new URLSearchParams(searchParams));
  };

  // Use getSearchParamValues() to get a list of param values for a given param name
  const getSearchParamValues = (paramName: string) =>
    searchParams
      .get(paramName)
      ?.split(",")
      .filter((value) => value !== "") || [];

  // Use hasSearchParamValue() to check if a value is selected for a given param name
  const hasSearchParamValue = (paramName: string, paramValue: string) => {
    return !!getSearchParamValues(paramName).find(
      (value) => value === paramValue,
    );
  };

  // Use setSearchParamValue() to replace all values for that param
  const setSearchParamValue = (paramName: string, paramValues: string) => {
    if (paramName !== AgathaSchemaParamsEnum.page) {
      searchParams.set(AgathaSchemaParamsEnum.page, DEFAULT_PAGE);
    }
    searchParams.set(paramName, paramValues);
    setSearchParams(new URLSearchParams(searchParams));
  };

  // Use toggleSearchParam() to toggle a single param value for a given param name
  const toggleSearchParamValue = (paramName: string, paramValue: string) => {
    if (paramName !== AgathaSchemaParamsEnum.page) {
      searchParams.set(AgathaSchemaParamsEnum.page, DEFAULT_PAGE);
    }

    const otherSelectedValues = getSearchParamValues(paramName).filter(
      (param) => param !== paramValue,
    );

    const isValueAlreadySelected = hasSearchParamValue(paramName, paramValue);
    if (isValueAlreadySelected) {
      if (otherSelectedValues.length === 0) {
        searchParams.delete(paramName);
      } else {
        searchParams.set(paramName, otherSelectedValues.join(","));
      }
    } else {
      searchParams.set(
        paramName,
        [...otherSelectedValues, paramValue].join(","),
      );
    }
    setSearchParams(new URLSearchParams(searchParams));
  };

  return {
    getAllSearchParams,
    setSearchParamValue,
    hasSearchParamValue,
    getSearchParamValues,
    toggleSearchParamValue,
    deleteSearchParamValues,
  };
};

interface IUseSearchParamsControls {
  searchParams: URLSearchParams;
  setSearchParams: Dispatch<SetStateAction<URLSearchParams>>;
}

useSearchParamsControls.displayName = "useSearchParamsControls";
export { useSearchParamsControls };
